import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useMutation } from "@apollo/client";
import Select from "react-select";
import { toast } from "react-toastify";
import { EDIT_USER_MUTATION } from "../../../graphql/mutations";

const EditUserModal = ({ show, user, handleClose, handleEnvChange, viewOnly, isLoading, isError, tenants, env, onUserUpdate }) => {
  const initTenantId = tenants?.find((tenant) => tenant.name === user?.tenantName)?.id;

  const [formData, setFormData] = useState({
    email: user?.email || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    tenantName: user?.tenantName || "",
    tenantId: initTenantId || "",
    roleName: user?.role?.name || "Viewer",
    isInActive: user?.isInactive?.toString() || "false"
  });

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        tenantName: user.tenantName,
        tenantId: initTenantId,
        roleName: user.role?.name || "Viewer",
        isInActive: user.isInactive?.toString() || "false"
      });
    }
  }, [user, initTenantId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleTenantChange = (selectedOption) => {
    if (!selectedOption) {
      setFormData((prevData) => ({
        ...prevData,
        tenantId: null,
        tenantName: ""
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        tenantId: selectedOption.value,
        tenantName: selectedOption.label
      }));
    }
  };

  const [editUser, { loading: mutationLoading }] = useMutation(EDIT_USER_MUTATION);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const { data } = await editUser({
        variables: {
          editUserInput: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            targetEmail: formData.email,
            tenantId: formData.tenantId,
            roleName: formData.roleName,
            isInactive: formData.isInActive === "true"
          }
        }
      });

      if (data?.editUser) {
        // Update user in the list immediately
        if (onUserUpdate) {
          onUserUpdate({
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            tenantName: formData.tenantName,
            tenantId: formData.tenantId,
            roleName: formData.roleName,
            isInactive: formData.isInActive === "true"
          });
        }

        handleClose();
        setTimeout(() => {
          toast.success(`User with ID: ${data?.editUser} edited successfully.`);
        }, 200);
      }
    } catch (error) {
      handleClose();
      setTimeout(() => {
        toast.error("Failed to edit user.");
      }, 200);
    }
  };

  const tenantOptions = tenants?.map((tenant) => ({
    value: tenant.id,
    label: tenant.name
  }));

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="d-flex justify-content-center">
        <Modal.Title className="w-100 text-center">{viewOnly ? "View User" : "Edit User"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {/* Email */}
          <Form.Group className="mb-3" controlId="formEmail">
            <Form.Label className="font-weight-bold">
              <strong>Email</strong>
            </Form.Label>
            <Form.Control type="email" name="email" value={formData.email} onChange={handleInputChange} readOnly disabled={viewOnly} />
          </Form.Group>

          {/* First Name */}
          <Form.Group className="mb-3" controlId="formFirstName">
            <Form.Label className="font-weight-bold">
              <strong>First Name</strong>
            </Form.Label>
            <Form.Control
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              disabled={viewOnly}
            />
          </Form.Group>

          {/* Last Name */}
          <Form.Group className="mb-3" controlId="formLastName">
            <Form.Label className="font-weight-bold">
              <strong>Last Name</strong>
            </Form.Label>
            <Form.Control type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required disabled={viewOnly} />
          </Form.Group>

          {/* Tenant Name */}
          <Form.Group className="mb-3" controlId="formTenantName">
            <Form.Label className="font-weight-bold">
              <strong>Tenant</strong>
            </Form.Label>
            {isLoading && <p>Loading tenants...</p>}
            {isError && <p className="text-danger">Failed to load tenants.</p>}
            {!isLoading && !isError && (
              <Select
                options={tenantOptions}
                onChange={handleTenantChange}
                value={tenantOptions?.find((option) => option.value === formData.tenantId) || null}
                placeholder="Search and select a tenant"
                isClearable
                isSearchable
                required
                isDisabled={viewOnly}
              />
            )}
          </Form.Group>

          {/* Role */}
          <Form.Group className="mb-3" controlId="formRole">
            <Form.Label className="font-weight-bold">
              <strong>Role</strong>
            </Form.Label>
            <Form.Control as="select" name="roleName" value={formData.roleName} onChange={handleInputChange} disabled={viewOnly}>
              <option value="Admin">Admin</option>
              <option value="Editor">Editor</option>
              <option value="Viewer">Viewer</option>
              <option value="Scenario">Scenario</option>
              <option value="Envana_Admin">Envana_Admin</option>
              <option value="EnvanaTrial">EnvanaTrial</option>
            </Form.Control>
          </Form.Group>

          {/* Is Inactive */}
          <Form.Group className="mb-3" controlId="formIsActive">
            <Form.Label className="font-weight-bold">
              <strong>Is Active?</strong>
            </Form.Label>
            <Form.Control as="select" name="isInActive" value={formData.isInActive} onChange={handleInputChange} disabled={viewOnly}>
              <option value="false">True</option>
              <option value="true">False</option>
            </Form.Control>
          </Form.Group>

          {!viewOnly && (
            <div className="d-grid">
              <Button variant="primary" type="submit" style={{ width: "50%" }} className="mx-auto" disabled={mutationLoading}>
                {mutationLoading ? "Editing user..." : "Save"}
              </Button>
            </div>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export { EditUserModal };
